import { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import { apiGet } from '../api/client';
import { useAuth } from '../hooks/useAuth';

type Status = 'pending' | 'success' | 'error';

export default function VerifyEmailPage() {
    const [searchParams] = useSearchParams();
    const { user, refresh } = useAuth();
    const [status, setStatus] = useState<Status>('pending');
    const [message, setMessage] = useState('');

    const nextPath = user && !user.profileComplete ? '/complete-signup' : '/available-badges';
    const nextLabel = user && !user.profileComplete ? 'Finish your profile' : 'Get Started';

    useEffect(() => {
        const token = searchParams.get('token');
        if (!token) {
            setStatus('error');
            setMessage('No verification token found in this link.');
            return;
        }

        apiGet<void>(`/auth/verify-email?token=${encodeURIComponent(token)}`)
            .then(async (res) => {
                if (res.success) {
                    await refresh(); // update session so emailVerified banner disappears
                    setStatus('success');
                } else {
                    setStatus('error');
                    setMessage(res.message ?? 'The verification link is invalid or has expired.');
                }
            })
            .catch(() => {
                setStatus('error');
                setMessage('Something went wrong. Please try again.');
            });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <Box minHeight="100vh" display="flex" alignItems="center" justifyContent="center" bgcolor="background.default">
            <Card sx={{ width: 420, border: 'none' }}>
                <CardContent sx={{ p: 4, textAlign: 'center' }}>
                    <WorkspacePremiumIcon sx={{ fontSize: 48, color: 'accent.main', mb: 1 }} />

                    {status === 'pending' && (
                        <>
                            <CircularProgress sx={{ mb: 2 }} />
                            <Typography variant="h6" fontWeight={700}>Verifying your email…</Typography>
                        </>
                    )}

                    {status === 'success' && (
                        <>
                            <CheckCircleOutlineIcon sx={{ fontSize: 56, color: 'success.main', mb: 1 }} />
                            <Typography variant="h5" fontWeight={700} mb={1}>Email Verified!</Typography>
                            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3 }}>
                                Your email address has been confirmed. You're all set.
                            </Typography>
                            <Button component={Link} to={nextPath} variant="contained" fullWidth>
                                {nextLabel}
                            </Button>
                        </>
                    )}

                    {status === 'error' && (
                        <>
                            <ErrorOutlineIcon sx={{ fontSize: 56, color: 'error.main', mb: 1 }} />
                            <Typography variant="h5" fontWeight={700} mb={1}>Verification Failed</Typography>
                            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3 }}>{message}</Typography>
                            <Button component={Link} to="/account" fullWidth>
                                Go to Account to Resend
                            </Button>
                        </>
                    )}
                </CardContent>
            </Card>
        </Box>
    );
}
